import { isEmpty, startCase } from 'lodash-es'
import { TFunction } from 'react-i18next'
import { cleanIbanValidationResponse } from 'pages/Contacts/utils/fiatInstruments'
import { IBAN_PATTERN, PayoutBeneficiaryFilters } from 'pages/Wallets/components/FiatSend/constants'
import { SourcePayWallet } from 'pages/Wallets/components/Pay/types'
import {
  AddBeneficiaryBankDetailsPayload,
  AddBeneficiaryBasicPayloadKeys,
  AddBeneficiaryFormPayload,
  AddBeneficiaryFormPayloadErrors,
  AddBeneficiaryPostResponse,
  AddBeneficiaryRequestPreRequest,
  AddIntermediaryBankDetailsPayload,
  Address,
  BeneficiaryDetails,
  BeneficiaryDetailsCurrency,
  BeneficiaryEntityType,
  BeneficiaryTransferDestination,
  BeneficiaryType,
  PaymentMethod,
  USDBeneficiaryOptions,
  USDBeneficiaryOptionsLabelMapping,
  ValidateIbanResponse,
  ValidateRoutingNumberResponse,
  ValidateScanResponse,
} from 'types/beneficiaries'
import { ContactEntity, ContactEntityType } from 'types/contacts'
import { BankAccount, Currencies } from 'types/types'
import { getAccountNumber, getBankCode } from './bankAccount'
import { titleCase } from './utils'

const CUBIX_ACCOUNT_NUMBER_PREFIX = '****'

export const resetCurrencySpecificFields = (
  currencyCode: BeneficiaryDetailsCurrency,
  resetField: (field: string) => void,
  beneficiaryPaymentMethod: PaymentMethod
) => {
  const commonFields = ['beneficiaryPaymentMethod']
  const ibanFields = ['iban', 'bic']
  const gbpFields = ['accountNumber', 'sortCode']
  const ngnFields = ['ngnAccountNumber', 'bankCode', 'bankName']
  const usdFields = ['usdAccountNumber', 'abaRoutingNumber', 'accountType', 'routingNumber']
  const usdCbitFields = ['cubixAccountNumberDigits']
  const zarFields = [
    'zarBank',
    'zarAccountNumber',
    'zarRoutingNumberBranchCode',
    'zarAccountType',
    'bankName',
  ]

  let fieldsToReset: string[] = []
  switch (currencyCode) {
    case BeneficiaryDetailsCurrency.EUR:
    case BeneficiaryDetailsCurrency.AED:
      fieldsToReset = [
        ...commonFields,
        ...gbpFields,
        ...ngnFields,
        ...usdFields,
        ...zarFields,
        ...usdCbitFields,
      ]
      break
    case BeneficiaryDetailsCurrency.GBP:
      fieldsToReset = [
        ...commonFields,
        ...ibanFields,
        ...ngnFields,
        ...usdFields,
        ...zarFields,
        ...usdCbitFields,
      ]
      break
    case BeneficiaryDetailsCurrency.NGN:
      fieldsToReset = [
        ...commonFields,
        ...ibanFields,
        ...gbpFields,
        ...usdFields,
        ...zarFields,
        ...usdCbitFields,
      ]
      break
    case BeneficiaryDetailsCurrency.USD:
      if (beneficiaryPaymentMethod === USDBeneficiaryOptions.CUBIX) {
        fieldsToReset = [...ibanFields, ...gbpFields, ...ngnFields, ...zarFields, ...usdFields]
      } else {
        fieldsToReset = [...ibanFields, ...gbpFields, ...ngnFields, ...zarFields, ...usdCbitFields]
      }
      break
    case BeneficiaryDetailsCurrency.ZAR:
      fieldsToReset = [
        ...commonFields,
        ...ibanFields,
        ...gbpFields,
        ...ngnFields,
        ...usdFields,
        ...usdCbitFields,
      ]
      break
    default:
      break
  }

  fieldsToReset.forEach(field => resetField(field))
}

const setCurrencyErrors = (
  currencyCode: BeneficiaryDetailsCurrency,
  error: AddBeneficiaryPostResponse,
  formErrors: AddBeneficiaryFormPayloadErrors,
  setFormStateError: React.Dispatch<React.SetStateAction<AddBeneficiaryFormPayloadErrors>>,
  beneficiaryPaymentMethod: PaymentMethod
) => {
  const message = error?.data?.details?.errors?.requestBody[0] || ''
  const errorFields = []

  switch (currencyCode) {
    case BeneficiaryDetailsCurrency.EUR:
    case BeneficiaryDetailsCurrency.AED:
      errorFields.push('iban', 'bic')
      break
    case BeneficiaryDetailsCurrency.GBP:
      errorFields.push('accountNumber', 'sortCode')
      break
    case BeneficiaryDetailsCurrency.NGN:
      errorFields.push('ngnAccountNumber', 'bankCode')
      break
    case BeneficiaryDetailsCurrency.USD:
      if (beneficiaryPaymentMethod === USDBeneficiaryOptions.CUBIX) {
        errorFields.push('cubixAccountNumberDigits')
      } else {
        errorFields.push('usdAccountNumber', 'abaRoutingNumber')
      }
      break
    case BeneficiaryDetailsCurrency.ZAR:
      errorFields.push('zarAccountNumber', 'zarRoutingNumberBranchCode')
      break
  }

  errorFields.forEach(field => {
    setFormStateError({
      ...formErrors,
      [field]: { message },
    })
  })
}

export const setInternationalBeneficiaryError = (
  errorMessage: string,
  errorFields: string[],
  formErrors: AddBeneficiaryFormPayloadErrors,
  setFormStateError: React.Dispatch<React.SetStateAction<AddBeneficiaryFormPayloadErrors>>
) => {
  errorFields.forEach(field => {
    setFormStateError({
      ...formErrors,
      [field]: { message: errorMessage },
    })
  })
}

export const setLocalBeneficiaryError = (
  currencyCode: BeneficiaryDetailsCurrency,
  error: AddBeneficiaryPostResponse,
  formErrors: AddBeneficiaryFormPayloadErrors,
  setFormStateError: React.Dispatch<React.SetStateAction<AddBeneficiaryFormPayloadErrors>>,
  beneficiaryPaymentMethod: PaymentMethod
) => {
  if (error?.data?.code === 'BEN-MAN-001') {
    setCurrencyErrors(currencyCode, error, formErrors, setFormStateError, beneficiaryPaymentMethod)
  }
}

export const getFieldsToValidateForCurrency = (
  currencyCode: BeneficiaryDetailsCurrency,
  beneficiaryEntityType: BeneficiaryEntityType,
  transferDestination: BeneficiaryTransferDestination,
  beneficiaryPaymentReference?: string,
  beneficiaryPaymentMethod?: PaymentMethod
) => {
  const validateFields: AddBeneficiaryBasicPayloadKeys[] = ['countryCode', 'currencyCode', 'alias']
  const beneficiaryAddressFields: AddBeneficiaryBasicPayloadKeys[] = [
    'addressLine1',
    'addressLine2',
    'city',
    'postCode',
    'region',
  ]
  if (transferDestination === BeneficiaryTransferDestination.INTERNATIONAL) {
    validateFields.push('intlIban', 'bicSwiftCode', ...beneficiaryAddressFields)

    if (currencyCode === BeneficiaryDetailsCurrency.USD) {
      validateFields.push('routingNumber')
    }
  } else {
    switch (currencyCode) {
      case BeneficiaryDetailsCurrency.EUR:
      case BeneficiaryDetailsCurrency.AED:
        validateFields.push('bic', ...beneficiaryAddressFields)
        break
      case BeneficiaryDetailsCurrency.GBP:
        validateFields.push('accountNumber', 'sortCode', ...beneficiaryAddressFields)
        break
      case BeneficiaryDetailsCurrency.NGN:
        validateFields.push('ngnAccountNumber', 'bankCode')
        break
      case BeneficiaryDetailsCurrency.USD:
        if (beneficiaryPaymentMethod === USDBeneficiaryOptions.CUBIX) {
          validateFields.push('cubixAccountNumberDigits')
        } else {
          validateFields.push(
            'usdAccountNumber',
            'usdAccountNumberConfirm',
            'abaRoutingNumber',
            'accountNumber',
            'accountType',
            ...beneficiaryAddressFields
          )
        }
        break
      case BeneficiaryDetailsCurrency.ZAR:
        validateFields.push(
          'zarBank',
          'zarAccountNumber',
          'zarAccountType',
          'zarRoutingNumberBranchCode'
        )
        break
      default:
        break
    }
  }

  switch (beneficiaryEntityType) {
    case BeneficiaryEntityType.INDIVIDUAL:
      validateFields.push('firstName', 'lastName', 'dateOfBirth')
      break
    case BeneficiaryEntityType.BUSINESS:
      validateFields.push('businessName')
      break
    default:
      break
  }

  if (beneficiaryPaymentReference) {
    validateFields.push('beneficiaryPaymentReference')
  }

  return validateFields
}

export const getIsGbpAccountDetialsValid = (scanValidationResponse: ValidateScanResponse) => {
  const validationErrors = scanValidationResponse?.validationFailed
  return {
    sortCode: !validationErrors?.some(errorMessage => errorMessage?.includes('Sort Code')),
    accountNumber: !validationErrors?.some(
      errorMessage => errorMessage?.includes('Account number') || errorMessage?.includes('Modulus')
    ),
  }
}

const getAccountNumberAndCode = (formValues: AddBeneficiaryFormPayload) => {
  const isSWIFT = formValues?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
  const isUSDAndUS =
    formValues?.currencyCode === BeneficiaryDetailsCurrency?.USD && formValues?.bankCountry === 'US'

  if (isSWIFT) {
    return {
      accountNumber: formValues.intlIban?.trim(),
      code: formValues.bicSwiftCode?.trim(),
      ...(isUSDAndUS && formValues?.routingNumber && { nid: formValues?.routingNumber?.trim() }),
    }
  } else {
    switch (formValues.currencyCode) {
      case BeneficiaryDetailsCurrency.GBP:
        return {
          accountNumber: formValues.accountNumber?.trim(),
          code: formValues.sortCode?.trim(),
        }
      case BeneficiaryDetailsCurrency.EUR:
      case BeneficiaryDetailsCurrency.AED:
        return {
          accountNumber: formValues.iban?.trim(),
          code: formValues.bic?.trim(),
        }
      case BeneficiaryDetailsCurrency.NGN:
        return {
          accountNumber: formValues.ngnAccountNumber?.trim(),
          code: formValues.bankCode?.trim(),
        }
      case BeneficiaryDetailsCurrency.USD:
        if (formValues?.beneficiaryPaymentMethod === USDBeneficiaryOptions.CUBIX) {
          return {
            accountNumber:
              `${CUBIX_ACCOUNT_NUMBER_PREFIX}${formValues?.cubixAccountNumberDigits}`?.trim(),
            code: formValues?.cbitBankCode?.trim(),
          }
        } else {
          return {
            accountNumber: formValues?.usdAccountNumber?.trim(),
            code: formValues?.abaRoutingNumber?.trim(),
            accountType: formValues?.accountType,
          }
        }
      case BeneficiaryDetailsCurrency.ZAR:
        return {
          accountNumber: formValues?.zarAccountNumber?.trim(),
          code: formValues?.zarRoutingNumberBranchCode?.trim(),
          accountType: formValues?.zarAccountType,
        }
      default:
        return {
          accountNumber: '',
          code: '',
        }
    }
  }
}

const getBankAccountDetails = (formValues: AddBeneficiaryFormPayload) => {
  const accountNumberAndCode = getAccountNumberAndCode(formValues)
  const isSWIFT = formValues?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
  const isNonCubixUsdBankAccount =
    formValues?.currencyCode === Currencies.USD &&
    !!formValues?.usdAccountNumber &&
    !!formValues?.abaRoutingNumber
  const isBankAddressRequired = isSWIFT || isNonCubixUsdBankAccount

  return {
    ...accountNumberAndCode,
    ...(isBankAddressRequired && formValues?.bankName && { bankName: formValues?.bankName }),
    ...(isBankAddressRequired && {
      bankAddress: {
        addressLine1: formValues?.bankAddressLine1,
        addressLine2: formValues?.bankAddressLine2,
        city: formValues?.bankCity,
        postCode: formValues?.bankPostCode,
        region: formValues?.bankRegion,
        country: formValues?.bankCountry,
      },
    }),
  }
}

const getIntermediaryBankDetials = (formValues: AddBeneficiaryFormPayload) => {
  if (
    formValues?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL &&
    !formValues?.skipIntermediaryBankScreen
  ) {
    const isSWIFT = formValues?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
    const isUSDAndUS =
      formValues?.currencyCode === BeneficiaryDetailsCurrency?.USD &&
      formValues?.intermediaryBankCountry === 'US'

    return {
      intermediaryBank: {
        code: formValues?.intermediaryBic,
        bankName: formValues?.intermediaryBankName,
        ...(isSWIFT &&
          isUSDAndUS &&
          formValues?.routingNumber && { nid: formValues?.routingNumber?.trim() }),
        bankAddress: {
          addressLine1: formValues?.intermediaryBankAddressLine1,
          addressLine2: formValues?.intermediaryBankAddressLine2,
          city: formValues?.intermediaryBankCity,
          postCode: formValues?.intermediaryBankPostCode,
          region: formValues?.intermediaryBankRegion,
          country: formValues?.intermediaryBankCountry,
        },
      },
    }
  }
  return null // Return null if skipIntermediaryBankScreen is true
}

export const orchestrateAddBeneficiaryPayload = (
  formValues: AddBeneficiaryFormPayload
): AddBeneficiaryRequestPreRequest => {
  const { addressLine1, addressLine2, city, region, postCode, dateOfBirth } = formValues || {}
  const hasAddress = !!addressLine1 || !!addressLine2 || !!city || !!region || !!postCode
  const [day, month, year] = dateOfBirth?.split(',') || []
  const hasDateParts = !!day && !!month && !!year
  const dateOfBirthFormatted = dateOfBirth && hasDateParts ? `${day}/${month}/${year}` : null

  return {
    alias: formValues?.alias,
    entityType: formValues?.beneficiaryEntityType,
    currency: formValues?.currencyCode,
    addressCountry: formValues?.countryCode,
    transferDestination: formValues?.transferDestination || BeneficiaryTransferDestination.LOCAL,
    beneficiaryType: formValues?.beneficiaryType,
    ...(formValues?.beneficiaryPaymentReference && {
      beneficiaryPaymentReference: formValues?.beneficiaryPaymentReference,
    }),
    ...(formValues?.currencyCode === BeneficiaryDetailsCurrency.USD && {
      paymentMethod: formValues?.beneficiaryPaymentMethod,
    }),
    fiat: {
      ...getBankAccountDetails(formValues),
      ...getIntermediaryBankDetials(formValues),
    },
    ...(hasAddress && {
      address: {
        addressLine1: !isEmpty(formValues?.addressLine1) ? formValues?.addressLine1 : null,
        addressLine2: !isEmpty(formValues?.addressLine2) ? formValues?.addressLine2 : null,
        city: !isEmpty(formValues?.city) ? formValues?.city : null,
        region: !isEmpty(formValues?.region) ? formValues?.region : null,
        postCode: !isEmpty(formValues?.postCode) ? formValues?.postCode : null,
        country: !isEmpty(formValues?.countryCode) ? formValues?.countryCode : null,
      },
    }),
    ...(formValues?.beneficiaryEntityType === BeneficiaryEntityType.INDIVIDUAL
      ? {
          firstName: formValues?.firstName,
          lastName: formValues?.lastName,
          ...(dateOfBirthFormatted && { dateOfBirth: dateOfBirthFormatted }),
        }
      : {
          businessName: formValues?.businessName,
        }),
  }
}

export const getLocalBankDetailsSummary = (
  formValues: AddBeneficiaryFormPayload,
  t: TFunction<'translation', undefined>
) => {
  const beneficiaryDetails = getBeneficiaryDetails(formValues, t)
  const currencyAccountDetails = getCurrencyAccountDetails(formValues, t)
  const accountCurrency = formValues?.currencyCode && {
    [t('accountCurrency')]: formValues?.currencyCode,
  }
  const countryCodeLabel =
    formValues?.beneficiaryEntityType === BeneficiaryEntityType.INDIVIDUAL
      ? t('manageBeneficiaries.countryOfResidence')
      : t('manageBeneficiaries.countryOfRegistration')

  const countryCode = formValues?.countryCode && {
    [countryCodeLabel]: formValues?.countryName ?? formValues?.countryCode,
  }

  const paymentReference = formValues?.beneficiaryPaymentReference && {
    [t('beneficiaryPaymentReference')]: formValues?.beneficiaryPaymentReference,
  }

  return {
    ...accountCurrency,
    ...countryCode,
    ...beneficiaryDetails,
    ...currencyAccountDetails,
    ...paymentReference,
  }
}

const getBeneficiaryTypeLabel = (
  beneficiaryType: BeneficiaryType | undefined,
  t: TFunction<'translation', undefined>
) => {
  switch (beneficiaryType) {
    case BeneficiaryType.FIRST_PARTY:
      return t('internal')
    case BeneficiaryType.THIRD_PARTY:
      return t('external')
    default:
      return ''
  }
}

const getBeneficiaryDetails = (
  formValues: AddBeneficiaryFormPayload,
  t: TFunction<'translation', undefined>
) => {
  const beneficiaryTypeLabel = getBeneficiaryTypeLabel(formValues?.beneficiaryType, t)

  const beneficiaryType = {
    ...(beneficiaryTypeLabel && {
      [t('manageBeneficiaries.beneficiaryType')]: beneficiaryTypeLabel,
    }),
  }

  switch (formValues?.beneficiaryEntityType) {
    case BeneficiaryEntityType.INDIVIDUAL:
      return {
        ...beneficiaryType,
        ...((formValues?.firstName || formValues?.lastName) && {
          [t('manageBeneficiaries.firstAndLastName')]:
            `${formValues?.firstName ?? ''} ${formValues?.lastName ?? ''}`,
        }),
        ...(formValues?.alias && {
          [t('manageBeneficiaries.beneficiaryDescription')]: formValues?.alias,
        }),
        ...(formValues?.dateOfBirth && {
          [t('beneficiaryDateOfBirth')]: formValues?.dateOfBirth?.split(',').join('/'),
        }),
      }
    case BeneficiaryEntityType.BUSINESS:
      return {
        ...beneficiaryType,
        ...(formValues?.businessName && {
          [t('businessName')]: formValues?.businessName,
        }),
        ...(formValues?.alias && {
          [t('manageBeneficiaries.beneficiaryDescription')]: formValues?.alias,
        }),
      }
  }
}

const getCurrencyAccountDetails = (
  formValues: AddBeneficiaryFormPayload,
  t: TFunction<'translation', undefined>
) => {
  switch (formValues?.currencyCode) {
    case Currencies.EUR:
    case Currencies.AED:
      return getIbanDetails(formValues, t)
    case Currencies.GBP:
      return getGbpDetails(formValues, t)
    case Currencies.USD:
      return getUsdDetails(formValues, t)
    case Currencies.NGN:
      return getNgnDetails(formValues, t)
    case Currencies.ZAR:
      return getZarDetails(formValues, t)
  }
}

const getIbanDetails = (
  formValues: AddBeneficiaryFormPayload,
  t: TFunction<'translation', undefined>
) => {
  const beneficiaryAddress = getBeneficiaryAddress(formValues)

  return {
    ...(formValues?.iban && { [t('iban')]: formValues.iban }),
    ...(formValues?.bic && { [t('bic')]: formValues.bic }),
    ...(beneficiaryAddress && { [t('address')]: beneficiaryAddress }),
  }
}

const getGbpDetails = (
  formValues: AddBeneficiaryFormPayload,
  t: TFunction<'translation', undefined>
) => {
  const beneficiaryAddress = getBeneficiaryAddress(formValues)

  return {
    ...(formValues?.accountNumber && { [t('accountNumber')]: formValues.accountNumber }),
    ...(formValues?.sortCode && { [t('sortCode')]: formValues.sortCode }),
    ...(beneficiaryAddress && { [t('address')]: beneficiaryAddress }),
  }
}

const getUsdDetails = (
  formValues: AddBeneficiaryFormPayload,
  t: TFunction<'translation', undefined>
) => {
  if (formValues?.beneficiaryPaymentMethod === USDBeneficiaryOptions.CUBIX) {
    return {
      ...(formValues?.cubixAccountNumberDigits && {
        [t('manageBeneficiaries.cubixAccountNumberLastFourDigits')]:
          `${CUBIX_ACCOUNT_NUMBER_PREFIX}${formValues.cubixAccountNumberDigits}`,
      }),
    }
  } else {
    const { abaRoutingNumber, usdAccountNumber, accountType, bankName, beneficiaryPaymentMethod } =
      formValues || {}

    const beneficiaryAddress = getBeneficiaryAddress(formValues)
    const bankAddress = getBankAddressAddress(formValues)

    return {
      ...(abaRoutingNumber && { [t('abaRoutingNumber')]: abaRoutingNumber }),
      ...(usdAccountNumber && { [t('accountNumber')]: usdAccountNumber }),
      ...(beneficiaryPaymentMethod && {
        [t('paymentMethod')]: USDBeneficiaryOptionsLabelMapping[beneficiaryPaymentMethod],
      }),
      ...(accountType && { [t('accountType')]: startCase(accountType?.toLowerCase()) }),
      ...(beneficiaryAddress && { [t('address')]: beneficiaryAddress }),
      ...(bankName && { [t('bankName')]: startCase(bankName?.toLowerCase()) }),
      ...(bankAddress && { [t('bankAddress')]: bankAddress }),
    }
  }
}

const getNgnDetails = (
  formValues: AddBeneficiaryFormPayload,
  t: TFunction<'translation', undefined>
) => {
  return {
    ...(formValues?.ngnAccountNumber && { [t('accountNumber')]: formValues.ngnAccountNumber }),
    ...(formValues?.bankName && { [t('bank')]: formValues?.bankName ?? formValues?.bankCode }),
  }
}

const getZarDetails = (
  formValues: AddBeneficiaryFormPayload,
  t: TFunction<'translation', undefined>
) => {
  return {
    ...(formValues?.zarAccountNumber && { [t('accountNumber')]: formValues.zarAccountNumber }),
    ...(formValues?.zarRoutingNumberBranchCode && {
      [t('routingNumberBranchCode')]: formValues.zarRoutingNumberBranchCode,
    }),
    ...(formValues?.zarAccountType && { [t('accountType')]: titleCase(formValues.zarAccountType) }),
    ...(formValues?.bankName && { [t('bank')]: formValues?.bankName ?? formValues?.zarBank }),
  }
}

export const getSwiftBankDetails = (
  formValues: AddBeneficiaryFormPayload,
  t: TFunction<'translation', undefined>
): {
  beneficiaryDetails: { [property: string]: string }
  beneficiaryBankDetails: { [property: string]: string }
  intermediaryBankDetails: { [property: string]: string }
} => {
  const beneficiaryDetails = getBeneficiaryDetails(formValues, t)
  const isUSD = formValues?.currencyCode === BeneficiaryDetailsCurrency.USD
  const isSWIFT = formValues?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL

  const beneficiaryAddress = [
    ...(formValues?.addressLine1 ? [formValues?.addressLine1] : []),
    ...(formValues?.addressLine2 ? [formValues?.addressLine2] : []),
    ...(formValues?.city ? [formValues?.city] : []),
    ...(formValues?.region ? [formValues?.region] : []),
    ...(formValues?.postCode ? [formValues?.postCode] : []),
    ...(formValues?.countryName ? [formValues?.countryName] : []),
  ]?.join(', ')

  const bankAddress = [
    ...(formValues?.bankAddressLine1 ? [formValues?.bankAddressLine1] : []),
    ...(formValues?.bankAddressLine2 ? [formValues?.bankAddressLine2] : []),
    ...(formValues?.bankCity ? [formValues?.bankCity] : []),
    ...(formValues?.bankRegion ? [formValues?.bankRegion] : []),
    ...(formValues?.bankPostCode ? [formValues?.bankPostCode] : []),
    ...(formValues?.bankCountry || formValues?.bankCountryName
      ? [formValues?.bankCountryName ?? formValues?.bankCountry]
      : []),
  ]?.join(', ')

  const intermediaryBankAddress = [
    ...(formValues?.intermediaryBankAddressLine1 ? [formValues?.intermediaryBankAddressLine1] : []),
    ...(formValues?.intermediaryBankAddressLine2 ? [formValues?.intermediaryBankAddressLine2] : []),
    ...(formValues?.intermediaryBankCity ? [formValues?.intermediaryBankCity] : []),
    ...(formValues?.intermediaryBankPostCode ? [formValues?.intermediaryBankPostCode] : []),
    ...(formValues?.intermediaryBankCountryName || formValues?.intermediaryBankCountry
      ? [formValues?.intermediaryBankCountryName ?? formValues?.intermediaryBankCountry]
      : []),
  ]?.join(', ')

  return {
    beneficiaryDetails: {
      ...beneficiaryDetails,
      ...(formValues?.currencyCode && {
        [t('accountCurrency')]: formValues?.currencyCode,
      }),
      ...(beneficiaryAddress && {
        [t('address')]: beneficiaryAddress,
      }),
    },
    beneficiaryBankDetails: {
      ...(formValues?.intlIban && { [t(isUSD ? 'accountNumber' : 'iban')]: formValues?.intlIban }),
      ...(formValues?.bicSwiftCode && { [t('bicSwiftCode')]: formValues?.bicSwiftCode }),
      ...(isUSD &&
        isSWIFT &&
        formValues?.routingNumber && { [t('abaRoutingNumber')]: formValues?.routingNumber }),
      ...(formValues?.bankName && { [t('bankName')]: formValues?.bankName }),
      ...(bankAddress && {
        [t('address')]: bankAddress,
      }),
    },
    intermediaryBankDetails: {
      ...(formValues?.intermediaryBankName && {
        [t('manageBeneficiaries.intermediarysBankName')]: formValues?.intermediaryBankName,
      }),
      ...(formValues?.intermediaryBic && {
        [t('bic')]: formValues?.intermediaryBic,
      }),
      ...(isUSD &&
        isSWIFT &&
        formValues?.intermediaryBankRoutingNumber && {
          [t('abaRoutingNumber')]: formValues?.intermediaryBankRoutingNumber,
        }),
      ...(intermediaryBankAddress && {
        [t('address')]: intermediaryBankAddress,
      }),
    },
  }
}

export const setFieldsFetchedFromIbanValidation = (
  ibanValidationResponse: ValidateIbanResponse
): AddBeneficiaryBankDetailsPayload => {
  const cleanDetail = cleanIbanValidationResponse(ibanValidationResponse)

  if (!cleanDetail?.validationFailed?.length) {
    const { bankCode, bankName, bankAddress, accountNumber } = cleanDetail?.bankAccount || {}

    return {
      ...(accountNumber && {
        intlIban: accountNumber,
      }),
      ...(bankCode && {
        bicSwiftCode: bankCode,
      }),
      ...(bankName && {
        bankName: bankName,
      }),
      ...(bankAddress?.addressLine1 && {
        bankAddressLine1: bankAddress?.addressLine1,
      }),
      ...(bankAddress?.addressLine2 && {
        bankAddressLine2: bankAddress?.addressLine2,
      }),
      ...(bankAddress?.city && {
        bankCity: bankAddress?.city,
      }),
      ...(bankAddress?.postCode && {
        bankPostCode: bankAddress?.postCode,
      }),
      ...(bankAddress?.region && {
        bankRegion: bankAddress?.region,
      }),
      ...(bankAddress?.countryCode && {
        bankCountry: bankAddress?.countryCode,
      }),
    }
  }
}

export const setBankFieldsFromRoutingNumberValidation = (
  routingNumberBankDetails: ValidateRoutingNumberResponse
): AddBeneficiaryBankDetailsPayload => {
  if (!routingNumberBankDetails?.validationFailed?.length) {
    const { bankName, bankAddress } = routingNumberBankDetails?.bankDetails || {}

    return {
      ...(bankName && {
        bankName: bankName,
      }),
      ...(bankAddress?.addressLine1 && {
        bankAddressLine1: bankAddress?.addressLine1?.trim(),
      }),
      ...(bankAddress?.addressLine2 && {
        bankAddressLine2: bankAddress?.addressLine2?.trim(),
      }),
      ...(bankAddress?.city && {
        bankCity: bankAddress?.city?.trim(),
      }),
      ...(bankAddress?.postCode && {
        bankPostCode: bankAddress?.postCode?.trim(),
      }),
      ...(bankAddress?.region && {
        bankRegion: bankAddress?.region?.trim(),
      }),
      ...(bankAddress?.countryCode && {
        bankCountry: bankAddress?.countryCode?.trim(),
      }),
    }
  }
}

export const setIntermediaryBankFieldsFromRoutingNumberValidation = (
  routingNumberBankDetails: ValidateRoutingNumberResponse
): AddIntermediaryBankDetailsPayload => {
  if (!routingNumberBankDetails?.validationFailed?.length) {
    const { bankName, bankAddress } = routingNumberBankDetails?.bankDetails || {}

    return {
      ...(bankName && {
        intermediaryBankName: bankName,
      }),
      ...(bankAddress?.addressLine1 && {
        intermediaryBankAddressLine1: bankAddress?.addressLine1?.trim(),
      }),
      ...(bankAddress?.addressLine2 && {
        intermediaryBankAddressLine2: bankAddress?.addressLine2?.trim(),
      }),
      ...(bankAddress?.city && {
        intermediaryBankCity: bankAddress?.city?.trim(),
      }),
      ...(bankAddress?.postCode && {
        intermediaryBankPostCode: bankAddress?.postCode?.trim(),
      }),
      ...(bankAddress?.region && {
        intermediaryBankRegion: bankAddress?.region?.trim(),
      }),
      ...(bankAddress?.countryCode && {
        intermediaryBankCountry: bankAddress?.countryCode?.trim(),
      }),
    }
  }
}

export const getEntityType = (
  beneficiaryEntityType: BeneficiaryEntityType,
  t: TFunction<'translation', undefined>
): string => {
  switch (beneficiaryEntityType) {
    case BeneficiaryEntityType.BUSINESS:
      return t('manageBeneficiaries.business')
    case BeneficiaryEntityType.INDIVIDUAL:
      return t('manageBeneficiaries.individual')
    default:
      return ''
  }
}

export const getBeneficiaryType = (
  beneficiaryType: BeneficiaryType,
  t: TFunction<'translation', undefined>
): string => {
  switch (beneficiaryType) {
    case BeneficiaryType.FIRST_PARTY:
      return t('internal')
    case BeneficiaryType.THIRD_PARTY:
      return t('external')
    default:
      return ''
  }
}

export const getBeneficiaryName = (beneficiary: BeneficiaryDetails): string => {
  return beneficiary?.entityType === BeneficiaryEntityType.INDIVIDUAL
    ? `${beneficiary?.firstName} ${beneficiary?.lastName}`
    : beneficiary?.businessName
}

export const getInternationalAccountNumberPlaceholder = (
  currency: string,
  t: TFunction<'translation', undefined>
) => {
  if (currency === Currencies.USD) {
    return t('inputPlaceholders.enterAccountNumberPlaceholder')
  } else {
    return t('inputPlaceholders.enterIbanPlaceholder')
  }
}

export const isIbanFormat = (value: string): boolean => {
  if (!value) return false

  const minLength = 15
  const maxLength = 34

  const isOverMinLength = value?.length >= minLength
  const isUnderMaxLength = value?.length <= maxLength

  return isOverMinLength && isUnderMaxLength && IBAN_PATTERN.test(value.toUpperCase())
}

export const getInternationalBankCodePlaceholder = (
  currency: string,
  t: TFunction<'translation', undefined>
) => {
  if (currency === Currencies.USD) {
    return t('inputPlaceholders.enterSwiftPlaceholder')
  } else {
    return t('inputPlaceholders.enterBicPlaceholder')
  }
}

export const getTransferDestinations = (
  supportedTransferDestinations: BeneficiaryTransferDestination[]
) => {
  return supportedTransferDestinations?.length
    ? supportedTransferDestinations
    : [BeneficiaryTransferDestination.LOCAL]
}

export const getAddress = (address: Address, addressCountryName?: string): string => {
  const { addressLine1, addressLine2, city, region, postCode, country } = address || {}
  return [
    ...(addressLine1 ? [startCase(addressLine1?.toLowerCase())] : []),
    ...(addressLine2 ? [startCase(addressLine2?.toLowerCase())] : []),
    ...(city ? [startCase(city?.toLowerCase())] : []),
    ...(region ? [startCase(region)] : []),
    ...(postCode ? [postCode] : []),
    ...(country || addressCountryName
      ? [addressCountryName ? startCase(addressCountryName?.toLowerCase()) : country]
      : []),
  ]?.join(', ')
}

const getBeneficiaryAddress = (formValues: AddBeneficiaryFormPayload) => {
  const { addressLine1, addressLine2, city, region, postCode, countryCode, countryName } =
    formValues || {}

  const hasAddress = addressLine1 || addressLine2 || city || region || postCode

  if (hasAddress) {
    const beneficiaryAddressData: Address = {
      addressLine1,
      addressLine2,
      city,
      postCode,
      region,
      country: countryCode,
    }

    return getAddress(beneficiaryAddressData, countryName)
  }
}

const getBankAddressAddress = (formValues: AddBeneficiaryFormPayload) => {
  const {
    bankAddressLine1,
    bankAddressLine2,
    bankCity,
    bankRegion,
    bankPostCode,
    bankCountry,
    bankCountryName,
  } = formValues || {}

  const hasAddress = bankAddressLine1 || bankAddressLine2 || bankCity || bankRegion || bankPostCode

  if (hasAddress) {
    const bankAddressData: Address = {
      addressLine1: bankAddressLine1,
      addressLine2: bankAddressLine2,
      city: bankCity,
      postCode: bankPostCode,
      region: bankRegion,
      country: bankCountry,
    }

    return getAddress(bankAddressData, bankCountryName)
  }
}

export const getAccountNumberAndBankCodeLabelKeys = (
  currency: BeneficiaryDetailsCurrency,
  transferDestination: BeneficiaryTransferDestination
) => {
  const isSWIFT = transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
  const isUSD = currency === BeneficiaryDetailsCurrency.USD

  if (isSWIFT) {
    if (isUSD) {
      return {
        accountNumberLabelKey: 'accountNumber',
        bankCodeLabelKey: 'swiftCode',
      }
    }

    return {
      accountNumberLabelKey: 'iban',
      bankCodeLabelKey: 'bic',
    }
  }

  switch (currency) {
    case BeneficiaryDetailsCurrency.EUR:
    case BeneficiaryDetailsCurrency.AED:
      return {
        accountNumberLabelKey: 'iban',
        bankCodeLabelKey: 'bic',
      }
    case BeneficiaryDetailsCurrency.GBP:
      return {
        accountNumberLabelKey: 'accountNumber',
        bankCodeLabelKey: 'sortCode',
      }
    case BeneficiaryDetailsCurrency.USD:
    case BeneficiaryDetailsCurrency.ZAR:
      return {
        accountNumberLabelKey: 'accountNumber',
        bankCodeLabelKey: 'abaRoutingNumber',
      }
    default:
      return {
        accountNumberLabelKey: 'accountNumber',
        bankCodeLabelKey: 'bankCode',
      }
  }
}

interface GetFiatSendBeneficiaryFiltersProps {
  selectedFilter?: string
  sourceWallet?: SourcePayWallet
  supportedFilters?: string[]
}

export const getFiatSendBeneficiaryFilters = ({
  selectedFilter,
  sourceWallet,
  supportedFilters,
}: GetFiatSendBeneficiaryFiltersProps) => {
  if (!selectedFilter) {
    const isThirdPartyBusinessPayoutSupported = supportedFilters?.includes(
      PayoutBeneficiaryFilters.BUSINESS
    )
    const isThirdPartyIndividualPayoutSupported = supportedFilters?.includes(
      PayoutBeneficiaryFilters.INDIVIDUAL
    )
    const isFirstPartyPayoutSupported = supportedFilters?.includes(
      PayoutBeneficiaryFilters.MY_COMPANY
    )

    const isThirdPartyPayoutsSupported =
      isThirdPartyIndividualPayoutSupported || isFirstPartyPayoutSupported

    return {
      ...(sourceWallet?.supportedTransferDestinations && {
        transferDestinations: sourceWallet?.supportedTransferDestinations,
      }),
      ...(sourceWallet?.currency && { currency: sourceWallet?.currency }),
      // fetch only BUSINESS
      ...(isThirdPartyBusinessPayoutSupported &&
        !isThirdPartyIndividualPayoutSupported && { entityType: BeneficiaryEntityType.BUSINESS }),
      // fetch only INDIVIDUAL
      ...(!isThirdPartyBusinessPayoutSupported &&
        isThirdPartyIndividualPayoutSupported && { entityType: BeneficiaryEntityType.INDIVIDUAL }),
      // fetch only THIRD_PARTY
      ...(isThirdPartyPayoutsSupported &&
        !isFirstPartyPayoutSupported && { beneficiaryType: BeneficiaryType.THIRD_PARTY }),
      // fetch only FIRST_PARTY
      ...(!isThirdPartyPayoutsSupported &&
        isFirstPartyPayoutSupported && { beneficiaryType: BeneficiaryType.FIRST_PARTY }),
    }
  }

  const isThirdPartyBusinessPayout = selectedFilter === PayoutBeneficiaryFilters.BUSINESS
  const isThirdPartyIndividualPayout = selectedFilter === PayoutBeneficiaryFilters.INDIVIDUAL
  const isFirstPartyPayout = selectedFilter === PayoutBeneficiaryFilters.MY_COMPANY

  if (isThirdPartyBusinessPayout) {
    return {
      beneficiaryType: BeneficiaryType.THIRD_PARTY,
      entityType: BeneficiaryEntityType.BUSINESS,
      transferDestinations: sourceWallet?.supportedTransferDestinations,
      currency: sourceWallet?.currency,
    }
  }

  if (isThirdPartyIndividualPayout) {
    return {
      beneficiaryType: BeneficiaryType.THIRD_PARTY,
      entityType: BeneficiaryEntityType.INDIVIDUAL,
      transferDestinations: sourceWallet?.supportedTransferDestinations,
      currency: sourceWallet?.currency,
    }
  }

  if (isFirstPartyPayout) {
    return {
      beneficiaryType: BeneficiaryType.FIRST_PARTY,
      entityType: BeneficiaryEntityType.BUSINESS,
      transferDestinations: sourceWallet?.supportedTransferDestinations,
      currency: sourceWallet?.currency,
    }
  }
}

export const mapPaymentMethodToBenficiaryDetails = (paymentMethod: BankAccount) => {
  return {
    currency: paymentMethod?.currency,
    beneficiaryType: BeneficiaryType.FIRST_PARTY,
    businessName: paymentMethod?.description,
    entityType: BeneficiaryEntityType.BUSINESS,
    fiat: {
      accountNumber: getAccountNumber(paymentMethod),
      code: getBankCode(paymentMethod),
      bankName: paymentMethod?.options?.bankName,
      bankAddress: paymentMethod?.options?.beneficiaryBank?.address,
      accountType: paymentMethod?.options?.accountType,
    },
    reference: paymentMethod?.id?.toString(),
    transferDestination: BeneficiaryTransferDestination.LOCAL,
  } as BeneficiaryDetails
}

export const getBeneficiaryToContactEntityMapping = (
  contactBeneficiary: BeneficiaryDetails
): ContactEntityType => {
  if (!contactBeneficiary?.entityType) return

  if (
    contactBeneficiary?.entityType === ContactEntity.COMPANY ||
    contactBeneficiary?.entityType === BeneficiaryEntityType.BUSINESS
  ) {
    return ContactEntity.COMPANY
  }

  return ContactEntity.INDIVIDUAL
}
